import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { isApiError } from '../../api/client';
import { partner } from '../../api/partner';
import type { Act, ReceivedHandoverDetail, Step } from '../../api/types';
import { canRespond, canWriteCases, useSession } from '../../app/session';
import { toast } from '../../app/toast';
import { ErrorBlock } from '../../components/ErrorBlock';
import { DownloadIcon } from '../../components/Icons';
import { Loading } from '../../components/Loading';
import { PageHeader } from '../../components/PageHeader';
import { useT, type DictKey } from '../../i18n';
import { openFile } from '../../lib/download';
import { describeError } from '../../lib/errors';
import { formatBytes, localName, useFormat } from '../../lib/format';
import { useAnnounceScreen } from '../../lib/problemContext';
import { useAsync, useOnVisible } from '../../lib/useAsync';
import { StepPanel } from '../steps/StepPanel';
import { DepartureWarning } from '../trips/DepartureWarning';
import { TripLink } from './TripLink';
import { routeText } from './HandoversPage';

/** 404 → [] (an endpoint of a newer API, or nothing to show yet). */
async function orEmpty<T>(p: Promise<T>, empty: T): Promise<T> {
  try {
    return await p;
  } catch (e) {
    if (isApiError(e) && (e.status === 404 || e.status === 405)) return empty;
    throw e;
  }
}

export interface HandoverData {
  h: ReceivedHandoverDetail;
  steps: Step[];
  acts: Act[] | null;
}

export async function loadHandover(id: string): Promise<HandoverData> {
  const h = await partner.handover(id);
  const [steps, acts] = await Promise.all([
    h.steps ? Promise.resolve(h.steps) : orEmpty(partner.steps({ kind: 'handover', id }).then((r) => r.items), [] as Step[]),
    orEmpty(partner.acts(id).then((r) => r.items as Act[] | null), null),
  ]);
  return { h, steps, acts };
}

export function HandoverPage() {
  const { id = '' } = useParams();
  const t = useT();
  const q = useAsync(() => loadHandover(id), [id]);
  useOnVisible(() => void q.reload());
  useAnnounceScreen(`/handovers/${id}`, { handover_id: id });

  if (q.loading && !q.data) return <Loading />;
  if (q.error && !q.data) return <ErrorBlock error={q.error} onRetry={() => void q.reload()} />;
  if (!q.data) return null;
  return <HandoverView data={q.data} reload={() => void q.reload()} t={t} />;
}

function HandoverView({ data, reload, t }: { data: HandoverData; reload: () => void; t: ReturnType<typeof useT> }) {
  const { h, steps, acts } = data;
  const me = useSession((s) => s.me);
  const f = useFormat();
  const [busy, setBusy] = useState(false);
  const route = routeText(h, f.country);
  const target = { kind: 'handover' as const, id: h.id };

  async function respond(action: 'accept' | 'decline') {
    if (action === 'decline' && !window.confirm(t('ho.declineConfirm'))) return;
    setBusy(true);
    try {
      await (action === 'accept' ? partner.accept(h.id) : partner.decline(h.id));
      toast(t(action === 'accept' ? 'ho.acceptedToast' : 'ho.declinedToast'));
      reload();
    } catch (e) {
      toast(t(describeError(e)), 'bad');
    } finally {
      setBusy(false);
    }
  }

  const download = (docId: string, kind: 'doc' | 'act' | 'mine') =>
    openFile(() =>
      kind === 'act' ? partner.actUrl(h.id, docId) : kind === 'doc' ? partner.handoverDocUrl(h.id, docId) : partner.documentUrl(docId),
    ).catch((e: unknown) => toast(t(describeError(e)), 'bad'));

  return (
    <>
      <PageHeader
        back="/handovers"
        title={h.case?.number ?? '—'}
        sub={
          <>
            {h.from_org?.name ? t('ho.from', { org: h.from_org.name }) : null}
            {' · '}
            {t(`ho.scope.${h.scope}` as DictKey)} · {t(`ho.status.${h.status}` as DictKey)}
          </>
        }
      />

      <div className="card space-y-2">
        {h.case?.deceased?.full_name ? (
          <p>
            <span className="text-[14px] text-lab">{t('ho.deceased')}</span>
            <br />
            <span className="text-[18px] font-semibold">{h.case.deceased.full_name}</span>
          </p>
        ) : null}
        {route ? (
          <p>
            <span className="text-[14px] text-lab">{t('ho.route')}</span>
            <br />
            {route}
          </p>
        ) : null}
        {h.dates?.window_from || h.dates?.window_to ? (
          <p className="text-[15px]">{t('ho.window', { from: f.date(h.dates.window_from) || '…', to: f.date(h.dates.window_to) || '…' })}</p>
        ) : null}
        <p className="text-[15px]">
          <span className="text-lab">{t('ho.progress')}: </span>
          {h.progress?.stage ? t(`ho.stage.${h.progress.stage}` as DictKey) : t('ho.noStage')}
        </p>
        {h.note ? (
          <p className="whitespace-pre-line text-[15px]">
            <span className="text-lab">{t('ho.note')}: </span>
            {h.note}
          </p>
        ) : null}
      </div>

      {h.status === 'offered' ? (
        canRespond(me) ? (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button type="button" className="btn-primary min-h-big text-[18px]" disabled={busy} onClick={() => void respond('accept')}>
              {t('ho.accept')}
            </button>
            <button type="button" className="btn-danger min-h-big text-[18px]" disabled={busy} onClick={() => void respond('decline')}>
              {t('ho.decline')}
            </button>
          </div>
        ) : (
          <p className="mt-4 text-[15px] text-lab">{t('ho.respondHint')}</p>
        )
      ) : null}

      {h.status === 'accepted' ? (
        <div className="mt-5">
          <StepPanel target={target} steps={steps} onChanged={reload} stage={h.progress?.stage} />
        </div>
      ) : steps.length ? (
        <div className="mt-5">
          <StepPanel target={target} steps={steps} onChanged={reload} stage={h.progress?.stage} disabled />
        </div>
      ) : null}

      <h2 className="sec">{t('ho.trip')}</h2>
      {h.partner_trips?.length ? (
        <ul className="space-y-2">
          {h.partner_trips.map((tr) => (
            <li key={tr.id} className="card text-[15px]">
              <span className="font-semibold">{tr.number ?? '—'}</span> · {t(`trips.status.${tr.status}` as DictKey)}
              {tr.vehicle_plate ? ` · ${tr.vehicle_plate}` : ''}
              {tr.driver_name ? ` · ${tr.driver_name}` : ''}
              {tr.status === 'planned' ? <DepartureWarning tripId={tr.id} /> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[15px] text-lab">{t('ho.noTrip')}</p>
      )}
      {h.status === 'accepted' && canWriteCases(me) ? <TripLink handover={h} onLinked={reload} /> : null}

      {acts && acts.length ? (
        <>
          <h2 className="sec">{t('ho.acts')}</h2>
          <ul className="space-y-2" data-testid="acts">
            {acts.map((a) => (
              <li key={a.id}>
                <button type="button" className="card tap-row" onClick={() => void download(a.id, 'act')}>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{t(a.kind === 'invoice' ? 'ho.invoice' : 'ho.act', { n: a.number ?? '' })}</span>
                    <span className="block text-[14px] text-lab">
                      {f.date(a.created_at, false)} · {t('ho.amountFree')}
                    </span>
                  </span>
                  <DownloadIcon />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {h.documents && h.documents.length ? (
        <>
          <h2 className="sec">{t('ho.docs')}</h2>
          <ul className="space-y-2">
            {h.documents.map((d) => (
              <li key={d.id}>
                <button type="button" className="card tap-row" onClick={() => void download(d.id, 'doc')}>
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{localName(d.document_type_name, f.locale) || d.file_name}</span>
                    {/* two documents of the same type differ by file name and date */}
                    <span className="block truncate text-[14px] text-lab">
                      {d.file_name} · {f.date(d.created_at, false)} · {formatBytes(d.size)}
                    </span>
                  </span>
                  <DownloadIcon />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {h.checklist && h.checklist.length ? (
        <>
          <h2 className="sec">{t('ho.checklist')}</h2>
          <ul className="card divide-y divide-line py-0">
            {h.checklist.map((c) => (
              <li key={c.document_type_key} className="flex min-h-tap items-center gap-2 py-2 text-[15px]">
                <span className="min-w-0 flex-1">{localName(c.name, f.locale)}</span>
                <span className={`chip ${c.status === 'accepted' ? 'text-ok' : c.status === 'missing' || c.status === 'rejected' ? 'text-warn' : ''}`}>
                  {t(`cl.${c.status}` as DictKey)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {h.my_documents?.length ? (
        <>
          <h2 className="sec">{t('ho.myDocs')}</h2>
          <ul className="space-y-2">
            {h.my_documents.map((d) => (
              <li key={d.id}>
                <button type="button" className="card tap-row" onClick={() => void download(d.id, 'mine')}>
                  <span className="min-w-0 flex-1 truncate">{d.file_name}</span>
                  <span className="text-[14px] text-lab">{f.date(d.created_at, false)}</span>
                  <DownloadIcon />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}
